import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, IButtonVariants } from 'components/button';
import { CardNews } from 'components/card';
import { Icon } from 'components/icon/icon';
import UrlToSvg from 'components/url-to-svg/url-to-svg';

import { FeaturedPagesFieldsFragment, Maybe, Page } from 'api/graphql-types';

import { Typography } from 'components/typography';

import styles from './featured-pages.module.scss';

export interface IFeaturedPagesProps {
	module: FeaturedPagesFieldsFragment;
}

const createAbsoluteSlug = (slug?: string): string => {
	if (!slug) return '';
	const slugIsAbsolute = slug.startsWith('/');
	return slugIsAbsolute ? slug : `/${slug}`;
};

const FeaturedPages = (props: IFeaturedPagesProps): React.JSX.Element => {
	const intl = useIntl();
	const [featuredPages, setFeaturedPages] = useState<Maybe<Page[]>>();
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const getPages = async (): Promise<void> => {
		setIsLoading(true);
		try {
			if (props.module.items && props.module.items.length > 0) {
				setFeaturedPages(props.module.items.map((item) => item.data.attributes as Page));
			}
		} catch (error: any) {
			// eslint-disable-next-line no-console
			console.error('Error in featured pages', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getPages();
	}, []);

	return (
		<div className={styles.FeaturedPages}>
			<div className={styles.wrapper}>
				<div className={styles.title}>
					{props.module?.featuredPagesTitle?.value && (
						<Typography tag={props.module?.featuredPagesTitle?.tag || 'h2'} variant="h2">
							{props.module?.featuredPagesTitle?.value}
						</Typography>
					)}
				</div>
				<div className={styles.content}>
					{featuredPages && featuredPages.length > 0
						? featuredPages.map(({ header, slug }: Page, index: number) => (
								<div key={index}>
									<CardNews
										id={`featured-pages-${header?.title}`}
										type="evenementen"
										bodyText={''}
										excerpt={header?.excerpt || ''}
										slug={createAbsoluteSlug(slug)}
										title={header?.title || ''}
										cover={{
											url: header?.cover?.data?.attributes?.url || '',
											alternativeText: header.cover?.data?.attributes?.alternativeText
										}}
										key={index}
										nestedTitle
									/>
								</div>
						  ))
						: !isLoading && <div>{intl.formatMessage({ id: 'modules.featuredPages.noPages' })}</div>}
				</div>
				{props.module?.cta && (
					<div className={styles.button}>
						<Button
							variant={
								props.module?.cta?.variant === 'tertiary'
									? 'tertiary-dark'
									: (props.module?.cta?.variant as unknown as IButtonVariants)
							}
							title={props.module?.cta?.ctaTitle || props.module?.cta?.href?.text || props.module?.cta?.href?.linkText}
							fullWidth
							href={props.module?.cta?.href?.url || props.module?.cta?.href?.linkUrl || '#'}
							icon={
								props.module?.cta?.iconLeft?.data || props.module?.cta?.iconRight?.data ? (
									<Icon
										customIcon={
											<UrlToSvg
												url={
													(props.module?.cta?.iconLeft?.data?.attributes?.url as string) ||
													(props.module.cta?.iconRight?.data?.attributes?.url as string)
												}
											/>
										}
										size="14px"
									/>
								) : undefined
							}
							iconPosition={
								props.module?.cta?.iconLeft?.data ? 'left' : props.module?.cta?.iconRight?.data ? 'right' : undefined
							}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default FeaturedPages;
